import React from 'react';
import { CompanySettings, ProInvoice } from '../../../types/pro';
import { INVOICE_STATUS, formatDate, krPrecise } from '../../../utils/pro';
import { invoiceAmounts, lineAmounts, paymentTermsLabel } from '../../../utils/invoice';
import { StatusChip } from '../StatusChip';
interface InvoiceDocumentProps {
  invoice: ProInvoice;
  company: CompanySettings;
}

/**
 * The document the customer receives. Same paper layout as the quote and the
 * quality report: company block left, reference block right, a ruled table,
 * and the totals pulled to the right edge.
 */
export function InvoiceDocument({
  invoice,
  company
}: InvoiceDocumentProps) {
  const sums = invoiceAmounts(invoice);
  const {
    billing
  } = invoice;
  const isCredit = Boolean(invoice.creditNoteOf);
  return <article className="mx-auto max-w-3xl overflow-hidden rounded-xl border border-stone-200 bg-white" data-id="element-278" data-mp="2ofqucbm505c">
      <header className="flex flex-wrap items-start justify-between gap-6 border-b border-stone-200 p-8" data-id="element-279" data-mp="3tv0hhuxra61u">
        <div data-id="element-280" data-mp="e2mm4ni7o793">
          <p className="text-xl font-bold tracking-tight text-stone-900" data-id="element-281" data-mp="e2w4box0la48">
            {company.name}
          </p>
          <p className="mt-1 text-sm leading-relaxed text-stone-500" data-id="element-282" data-mp="j1za5bktjkd9">
            {company.address}
            <br data-id="element-283" data-mp="n1znesw6lpiq" />
            {company.postalCode} {company.city}
            <br data-id="element-284" data-mp="ru17h0zlckjb" />
            CVR {company.cvr}
            <br data-id="element-285" data-mp="ruspta46glrn" />
            {company.phone} · {company.email}
          </p>
        </div>
        <div className="text-right" data-id="element-286" data-mp="ws2mnatwbzpi">
          <p className="text-xs font-semibold uppercase tracking-wider text-stone-400" data-id="element-287" data-mp="11rp1tptabq0y">
            {isCredit ? 'Kreditnota' : 'Faktura'}
          </p>
          <p className="font-mono text-lg font-bold text-stone-900" data-id="element-288" data-mp="16s3ana2pn401">
            {invoice.invoiceNo}
          </p>
          <div className="mt-1.5 flex justify-end" data-id="element-289" data-mp="1arkha6tvi76s">
            <StatusChip meta={INVOICE_STATUS[invoice.status]} data-id="element-290" data-mp="38in1se7axm4g" />
          </div>
          <p className="mt-2 text-sm leading-relaxed text-stone-500" data-id="element-291" data-mp="3cjqlxcwdu4bh">
            Fakturadato {formatDate(invoice.invoiceDate)}
            <br data-id="element-292" data-mp="jogmp15pylsy" />
            Forfaldsdato {formatDate(invoice.dueDate)}
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-6 border-b border-stone-200 p-8 sm:grid-cols-2" data-id="element-293" data-mp="ergpzgurevei">
        <div data-id="element-294" data-mp="sqzjp4mtq47a">
          <p className="text-xs font-semibold uppercase tracking-wider text-stone-400" data-id="element-295" data-mp="srhqgv7440tg">
            Faktureres til
          </p>
          <p className="mt-1.5 text-base font-semibold text-stone-900" data-id="element-296" data-mp="oqpgfzqf1rw4">
            {billing.name || invoice.customerName || '—'}
          </p>
          <p className="text-sm leading-relaxed text-stone-500" data-id="element-297" data-mp="xpszx4thw9b5">
            {billing.attention && <>
                Att. {billing.attention}
                <br data-id="element-298" data-mp="17eufitw4oool" />
              </>}
            {billing.address}
            {billing.address && <br data-id="element-299" data-mp="1lelogg7sfevq" />}
            {billing.postalCode} {billing.city}
            {billing.cvr && <>
                <br data-id="element-300" data-mp="1gg0yd9m92bl0" />
                CVR {billing.cvr}
              </>}
            {billing.email && <>
                <br data-id="element-301" data-mp="201u53wjv8cf3" />
                {billing.email}
              </>}
          </p>
        </div>

        <dl className="space-y-1.5 text-sm sm:text-right" data-id="element-302" data-mp="24zm75je85ex0">
          {invoice.reference && <div className="sm:flex sm:justify-end sm:gap-3" data-id="element-303" data-mp="1r033p3wcyrr1">
              <dt className="text-stone-400" data-id="element-304" data-mp="1v1g8danpsowd">Reference</dt>
              <dd className="font-medium text-stone-900" data-id="element-305" data-mp="1v42dl3n4usbf">
                {invoice.reference}
              </dd>
            </div>}
          {invoice.orderNo && <div className="sm:flex sm:justify-end sm:gap-3" data-id="element-306" data-mp="1m34acnkiqotw">
              <dt className="text-stone-400" data-id="element-307" data-mp="17des1fkao46e">Ordrenummer</dt>
              <dd className="font-medium text-stone-900" data-id="element-308" data-mp="17asm1kd1xwqa">{invoice.orderNo}</dd>
            </div>}
          <div className="sm:flex sm:justify-end sm:gap-3" data-id="element-309" data-mp="ybys61r3vkgf">
            <dt className="text-stone-400" data-id="element-310" data-mp="ycgwllnx94zf">Betalingsbetingelser</dt>
            <dd className="font-medium text-stone-900" data-id="element-311" data-mp="13bbfsopswvk2">
              {paymentTermsLabel(invoice.paymentTerms, invoice.paymentTermsDays)}
            </dd>
          </div>
        </dl>
      </div>

      <table className="w-full text-sm" data-id="element-312" data-mp="qc13l30dwqat">
        <thead data-id="element-313" data-mp="lk0zqzh71d8y">
          <tr className="border-b border-stone-200 text-left" data-id="element-314" data-mp="ljrc3ijmfi6f">
            <th className="px-8 py-2.5 text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-315" data-mp="hixj4arbnd6o">
              Beskrivelse
            </th>
            <th className="px-2 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-316" data-mp="cknoq2df8gm4">
              Antal
            </th>
            <th className="px-2 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-317" data-mp="2lopfaejpxbs">
              Stk. pris
            </th>
            <th className="px-2 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-318" data-mp="3uqsjoxp2bjob">
              Moms
            </th>
            <th className="px-8 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-319" data-mp="1sy4cyed7me3s">
              Beløb
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-100" data-id="element-320" data-mp="3atmx1mqz9cvi">
          {invoice.lines.map(line => {
          const amounts = lineAmounts(line, invoice.pricesIncludeVat);
          return <tr key={line.id} data-id="element-321" data-mp="3knnaqd0brqfu">
                <td className="px-8 py-2.5 text-stone-900" data-id="element-322" data-mp="3omw2jcbt1e8r">
                  {line.description || '—'}
                  {amounts.discountAmount > 0 && <span className="ml-2 text-xs text-emerald-600" data-id="element-323" data-mp="2rypqw5ggtlkz">
                      −
                      {line.discountType === 'procent' ? `${line.discount} %` : krPrecise(line.discount)}
                    </span>}
                </td>
                <td className="whitespace-nowrap px-2 py-2.5 text-right tabular-nums text-stone-600" data-id="element-324" data-mp="35ujuc8ygziqp">
                  {line.quantity.toLocaleString('da-DK')} {line.unit}
                </td>
                <td className="whitespace-nowrap px-2 py-2.5 text-right tabular-nums text-stone-600" data-id="element-325" data-mp="l8i9ew74ild9">
                  {krPrecise(line.unitPrice)}
                </td>
                <td className="whitespace-nowrap px-2 py-2.5 text-right tabular-nums text-stone-500" data-id="element-326" data-mp="2d76t05127k5f">
                  {Math.round(line.vatRate * 100)} %
                </td>
                <td className="whitespace-nowrap px-8 py-2.5 text-right font-medium tabular-nums text-stone-900" data-id="element-327" data-mp="2m84xeofettoj">
                  {krPrecise(amounts.net)}
                </td>
              </tr>;
        })}
          {invoice.lines.length === 0 && <tr data-id="element-328" data-mp="1ti4tarsqgkmq">
              <td colSpan={5} className="px-8 py-10 text-center text-sm text-stone-400" data-id="element-329" data-mp="288lty8xjn6ik">
                Ingen linjer på fakturaen endnu
              </td>
            </tr>}
        </tbody>
      </table>

      <div className="border-t border-stone-200 p-8" data-id="element-330" data-mp="1fqqaakytsjfo">
        <dl className="ml-auto max-w-xs space-y-1.5 text-sm" data-id="element-331" data-mp="1au9ldojsy11j">
          <div className="flex justify-between" data-id="element-332" data-mp="1arnd1mp37hi9">
            <dt className="text-stone-500" data-id="element-333" data-mp="3mfs7x6aax4td">Subtotal</dt>
            <dd className="tabular-nums text-stone-900" data-id="element-334" data-mp="3r7joqp3nnw6b">
              {krPrecise(sums.subtotal)}
            </dd>
          </div>
          {invoice.totalDiscount > 0 && <div className="flex justify-between" data-id="element-335" data-mp="3dhzrfysufr4e">
              <dt className="text-stone-500" data-id="element-336" data-mp="3hgyu1cr3rbso">
                Rabat {invoice.totalDiscount} %
              </dt>
              <dd className="tabular-nums text-emerald-600" data-id="element-337" data-mp="93016jzfg6je">
                −{krPrecise(sums.invoiceDiscount)}
              </dd>
            </div>}
          <div className="flex justify-between" data-id="element-338" data-mp="18tj9lbtpmw">
            <dt className="text-stone-500" data-id="element-339" data-mp="gzi37dnzyq">Moms</dt>
            <dd className="tabular-nums text-stone-900" data-id="element-340" data-mp="50bsccapqzmf">
              {krPrecise(sums.vat)}
            </dd>
          </div>
          <div className="flex justify-between border-t border-stone-200 pt-1.5 text-base font-bold" data-id="element-341" data-mp="2kr7yrbazlklt">
            <dt className="text-stone-900" data-id="element-342" data-mp="2pquafgb62ssi">Total</dt>
            <dd className="tabular-nums text-stone-900" data-id="element-343" data-mp="2po7v2ui1b8zq">
              {krPrecise(sums.total)}
            </dd>
          </div>
          {sums.paid > 0 && <>
              <div className="flex justify-between" data-id="element-344" data-mp="3cdhk82iiu7cb">
                <dt className="text-stone-500" data-id="element-345" data-mp="37ffzze9d01z9">Betalt</dt>
                <dd className="tabular-nums text-emerald-600" data-id="element-346" data-mp="37i25l8cow7eu">
                  −{krPrecise(sums.paid)}
                </dd>
              </div>
              <div className="flex justify-between border-t border-stone-200 pt-1.5 font-bold" data-id="element-347" data-mp="3zr2ox7blzc4">
                <dt className="text-stone-900" data-id="element-348" data-mp="3v50la3szjbbp">Rest at betale</dt>
                <dd className="tabular-nums text-stone-900" data-id="element-349" data-mp="3v7wifpx0dvz3">
                  {krPrecise(sums.outstanding)}
                </dd>
              </div>
            </>}
        </dl>

        {invoice.notes.trim() && <p className="mt-8 whitespace-pre-line border-t border-stone-100 pt-5 text-sm leading-relaxed text-stone-700" data-id="element-350" data-mp="mpgwpi39eh2z">
            {invoice.notes}
          </p>}

        <div className="mt-8 border-t border-stone-100 pt-5 text-xs leading-relaxed text-stone-500" data-id="element-351" data-mp="hx77y5dvrn2a">
          <p className="font-semibold text-stone-700" data-id="element-352" data-mp="pcj7y4pm28kn">Betalingsoplysninger</p>
          <p className="mt-1" data-id="element-353" data-mp="u9hykda2fxe3">
            Beløbet bedes indbetalt senest {formatDate(invoice.dueDate)} med
            angivelse af fakturanummer {invoice.invoiceNo}.
            <br data-id="element-354" data-mp="148gwp1x7gamu" />
            Bank: Danske Bank · Reg. 1551 · Konto 4728 1193 06
            <br data-id="element-355" data-mp="189kf9w6n0krr" />
            MobilePay erhverv: 40 55 12 · CVR {company.cvr}
          </p>
          {company.invoiceFooter && <p className="mt-3" data-id="element-356" data-mp="1d6svhr886eqd">{company.invoiceFooter}</p>}
        </div>
      </div>
    </article>;
}